import {
    fetchUserAttributes,
    resetPassword,
    signOut,
    signIn,
    updateUserAttributes,
    confirmResetPassword,
    fetchAuthSession,
    getCurrentUser,
} from "aws-amplify/auth";
import { organizationActions, loaderActions } from "../";
import { LoaderContent } from "../../../../Utils/Loader";
import { localStorageSelectOrgUuid } from "../../../../Utils/constants";
import { getAmplifyAuthErrorCode } from "../../../../Utils/amplifyAuth";

export const SET_USER_EMAIL = "SET_USER_EMAIL";
export const SET_USER_NICKNAME = "SET_USER_NICKNAME";
export const SET_APP_INITIALIZED = "SET_APP_INITIALIZED";
export const SIGNOUT = "SIGNOUT";
export const SET_JWT_EXP = "SET_JWT_EXP";
export const SET_USER_ATTRIBUTES = "SET_USER_ATTRIBUTES";
export const SET_IS_MM_CONNECTED = "SET_IS_MM_CONNECTED";
const USER_ALREADY_AUTHENTICATED_EXCEPTION = "UserAlreadyAuthenticatedException";
const USER_UNAUTHENTICATED_EXCEPTION = "UserUnAuthenticatedException";

export const setUserEmail = (email) => ({ type: SET_USER_EMAIL, payload: email });
export const setUserNickname = (nickname) => ({ type: SET_USER_NICKNAME, payload: nickname });
export const setIsMMConnected = (isConnected) => ({
    type: SET_IS_MM_CONNECTED,
    payload: isConnected,
});
export const setAppInitialized = (isInitialized) => ({
    type: SET_APP_INITIALIZED,
    payload: isInitialized,
});
export const resetReduxOnSignout = () => ({ type: SIGNOUT });
export const setJWTExp = (exp) => ({ type: SET_JWT_EXP, payload: exp });

const getCurrentAuthenticatedSession = async () => {
    try {
        await getCurrentUser();
    } catch (error) {
        if (getAmplifyAuthErrorCode(error) === USER_UNAUTHENTICATED_EXCEPTION) {
            return null;
        }
        throw error;
    }

    const session = await fetchAuthSession();
    if (!session?.tokens?.accessToken && !session?.tokens?.idToken) {
        return null;
    }
    return session;
};

const parsePublisherTnC = (publisherTnCAttribute) => {
    if (!publisherTnCAttribute) {
        return {};
    }

    try {
        return JSON.parse(publisherTnCAttribute);
    } catch {
        return {};
    }
};

const mapAuthenticatedUser = async (session, dispatch) => {
    const userAttributes = await fetchUserAttributes();
    const idToken = session.tokens?.idToken;
    const accessToken = session.tokens?.accessToken;
    const exp = idToken?.payload?.exp || accessToken?.payload?.exp || "";

    dispatch(setJWTExp(exp));

    return {
        nickname: userAttributes?.nickname,
        email: userAttributes?.email,
        email_verified:
            userAttributes?.email_verified === true || userAttributes?.email_verified === "true",
        token: idToken?.toString() || accessToken?.toString(),
        publisherTnC: parsePublisherTnC(userAttributes?.["custom:publisher_tnc"]),
    };
};

export const fetchAuthenticatedUser = () => async (dispatch) => {
    const session = await getCurrentAuthenticatedSession();
    if (!session) {
        dispatch(resetReduxOnSignout());
        throw new Error("User is not authenticated");
    }

    return await mapAuthenticatedUser(session, dispatch);
};

export const initializeApplication = () => async (dispatch) => {
    // console.log("initializeApplication: start");
    try {
        const session = await getCurrentAuthenticatedSession();
        if (!session) {
            dispatch(resetReduxOnSignout());
            return;
        }

        const authenticatedUser = await mapAuthenticatedUser(session, dispatch);
        dispatch(
            setUserAttributes({
                isLoggedIn: true,
                email: authenticatedUser.email,
                nickname: authenticatedUser.nickname,
                isEmailVerified: authenticatedUser.email_verified,
                publisherTnC: authenticatedUser.publisherTnC,
            })
        );
        await dispatch(organizationActions.initializeOrg());
    } catch (error) {
        console.error("initialize application error: ", error);
    } finally {
        dispatch(setAppInitialized(true));
    }
};

export const handleUserNotConfirmed = (email) => async (dispatch) => {
    return await dispatch(setUserEmail(email));
};

export const setUserAttributes = (userAttributes) => (dispatch) => {
    dispatch({ type: SET_USER_ATTRIBUTES, payload: userAttributes });
};

export const updateUserTnCAttribute = (tncAgreementVesrion) => async (dispatch) => {
    const tncValue = { ver: tncAgreementVesrion, accepted: true };
    try {
        await updateUserAttributes({
            userAttributes: { "custom:publisher_tnc": JSON.stringify(tncValue) },
        });
        await dispatch(setUserAttributes({ publisherTnC: tncValue }));
    } catch (error) {
        throw error;
    }
};

export const login = (email, password) => async (dispatch) => {
    try {
        dispatch(loaderActions.startAppLoader(LoaderContent.LOGIN));
        const signInResult = await signIn({ username: email, password });
        const nextSignInStep = signInResult?.nextStep?.signInStep;
        switch (nextSignInStep) {
            case "CONFIRM_SIGN_UP": {
                await dispatch(handleUserNotConfirmed(email));
                break;
            }
            case "RESET_PASSWORD": {
                dispatch(setUserEmail(email));
                break;
            }
            case "DONE": {
                await dispatch(initializeApplication());
                break;
            }
            default: {
                break;
            }
        }
        return { nextSignInStep };
    } catch (error) {
        switch (getAmplifyAuthErrorCode(error)) {
            case USER_ALREADY_AUTHENTICATED_EXCEPTION: {
                return await dispatch(initializeApplication());
            }
            default: {
                break;
            }
        }
        throw error;
    } finally {
        dispatch(loaderActions.stopAppLoader());
    }
};

export const signout = async (dispatch) => {
    localStorage.removeItem(localStorageSelectOrgUuid);
    dispatch(loaderActions.startAppLoader(LoaderContent.SIGN_OUT));
    await signOut();
    dispatch(resetReduxOnSignout());
    dispatch(loaderActions.stopAppLoader());
};

export const forgotPassword = (email) => async (dispatch) => {
    try {
        dispatch(loaderActions.startAppLoader(LoaderContent.FORGOT_PASSWORD));
        await resetPassword({ username: email });
        dispatch(loaderActions.stopAppLoader());
    } catch (e) {
        dispatch(loaderActions.stopAppLoader());
        throw e;
    }
};

export const forgotPasswordSubmit = (email, code, password) => async (dispatch) => {
    try {
        dispatch(loaderActions.startAppLoader(LoaderContent.FORGOT_PASSWORD_SUBMIT));
        await confirmResetPassword({
            username: email,
            confirmationCode: code,
            newPassword: password,
        });
        dispatch(loaderActions.stopAppLoader());
    } catch (e) {
        dispatch(loaderActions.stopAppLoader());
        throw e;
    }
};
